import { Group } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  useMantineReactTable
} from "mantine-react-table-open";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { compareByComparisonOperators } from "../../../../../utils/compareByComparisonOperators";
import { THEME_VARS } from "../../../../../utils/constants";
import { sortBackendDates } from "../../../../../utils/dates/sortBackendDates";
import type { EnergyDataImportRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import {
  EnergyDataFileImportRequestModalType,
  EnergyDataFileImportRequestStatus,
  getTypeOfDataLabel
} from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { NameCell } from "./NameCell";
import { StatusCell } from "./StatusCell";
import { SupportCell } from "./SupportCell";

enum TableColumn {
  Name = "energyDataFile__name",
  Status = "status",
  Type = "typeOfData",
  Created = "created",
  Support = "supportRequested"
}

interface EnergyDataImportRequestsTableProps {
  count: number;
  data: Array<EnergyDataImportRequest>;
  loading?: boolean;
  pageIndex: number;
  pageSize: number;
  selection: Array<EnergyDataImportRequest>;
  toolbarComponents?: React.ReactNode;
  onFetchData: ({
    pageIndex,
    pageSize
  }: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  onSelectionChange: (newSelection: Array<EnergyDataImportRequest>) => void;
  onSetModal: (
    type: EnergyDataFileImportRequestModalType,
    isOpen: boolean,
    selectedImportRequest?: EnergyDataImportRequest
  ) => void;
}

function EnergyDataImportRequestsTable({
  count,
  data,
  loading = false,
  pageIndex,
  pageSize,
  selection,
  onFetchData,
  onSelectionChange,
  toolbarComponents,
  onSetModal
}: EnergyDataImportRequestsTableProps) {
  const cols: Array<MRT_ColumnDef<EnergyDataImportRequest>> = [
    {
      accessorKey: TableColumn.Name,
      header: "Name",
      enableCellHoverReveal: true,
      Cell: ({ row }) => <NameCell file={row.original} setModal={onSetModal} />,
      sortingFn: (rowA, rowB) =>
        compareByComparisonOperators(
          rowA.original.energyDataFile.name,
          rowB.original.energyDataFile.name
        )
    },
    {
      accessorKey: TableColumn.Type,
      header: "Typ",
      size: 150,
      grow: false,
      enableSorting: false,
      Cell: ({ row }) => getTypeOfDataLabel(row.original.typeOfData)
    },
    {
      accessorKey: TableColumn.Status,
      header: "Status",
      size: 240,
      grow: false,
      Cell: ({ row }) => <StatusCell file={row.original} />
    },
    {
      accessorKey: TableColumn.Created,
      header: "Hochgeladen am",
      size: 180,
      grow: false,
      sortingFn: (rowA, rowB) =>
        sortBackendDates(rowA.original.created, rowB.original.created)
    },
    {
      accessorKey: TableColumn.Support,
      header: "Supportanfrage",
      size: 140,
      grow: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <SupportCell
          file={row.original}
          onRequestSupport={() =>
            onSetModal(
              EnergyDataFileImportRequestModalType.Support,
              true,
              row.original
            )
          }
        />
      )
    }
  ];

  const table = useMantineReactTable<EnergyDataImportRequest>({
    ...getDefaultMRTOptions({ hideBorder: true }),
    layoutMode: "grid",
    displayColumnDefOptions: {
      "mrt-row-select": {
        size: 40,
        minSize: 40
      }
    },
    state: {
      isLoading: loading,
      pagination: { pageIndex, pageSize },
      rowSelection: selection.reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: true
        }),
        {} as MRT_RowSelectionState
      )
    },
    mantineTableBodyRowProps: ({ row }) => ({
      bg:
        row.original.status === EnergyDataFileImportRequestStatus.Stopped
          ? THEME_VARS.warning[0]
          : undefined
    }),
    enableRowSelection: (row) =>
      row.original.status !== EnergyDataFileImportRequestStatus.InProgress,
    onRowSelectionChange: (newSelection) => {
      if (typeof newSelection === "function") {
        const prevSelectionState = table.getState().rowSelection;
        const newSelectionState = newSelection(prevSelectionState);
        const newSelectionArray = Object.keys(newSelectionState).reduce(
          (acc, key) => {
            if (newSelectionState[key]) {
              const selectedItem = data?.find((item) => item.id === key);
              if (selectedItem) {
                acc.push(selectedItem);
              }
            }
            return acc;
          },
          [] as Array<EnergyDataImportRequest>
        );
        onSelectionChange(newSelectionArray);
      }
    },

    manualPagination: true,
    rowCount: count,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex, pageSize };
        const newState = pagination(currentState);
        onFetchData({
          pageIndex: newState.pageIndex,
          pageSize: newState.pageSize
        });
      }
    },
    renderTopToolbarCustomActions: () => (
      <Group gap="xs" justify="flex-end" w="stretch">
        {toolbarComponents}
      </Group>
    ),

    columns: cols,
    data: data || []
  });

  return <MantineReactTable table={table} />;
}

export { EnergyDataImportRequestsTable };
